/**
 * City-surface auth identity for the DES-60 authed-on-city chrome swap (WEB-640).
 *
 * Pure helpers — safe for SSR, client, Storybook, and tests. Callers that need
 * a live probe use {@link resolveCityAuthUser} (server) or RTK `getCurrentUser`
 * (client); both funnel through {@link cityAuthUserFromUnknown}.
 */

export interface CityAuthUser {
  /** Display name for the header affordance + menu identity card. */
  readonly displayName: string;
  /**
   * Mono meta / stats line under the name. Built only from real fields
   * (location · Joined Mon YYYY · email). Never invents counts.
   */
  readonly meta: string;
  /** Uppercase initials for the gradient avatar fallback. */
  readonly initials: string;
  /** Absolute avatar URL when present; otherwise `null` (initials render). */
  readonly avatarUrl: string | null;
}

interface RawUserFields {
  readonly id?: unknown;
  readonly email?: unknown;
  readonly name?: unknown;
  readonly firstName?: unknown;
  readonly lastName?: unknown;
  readonly location?: unknown;
  readonly createdAt?: unknown;
  readonly avatar?: unknown;
  readonly picture?: unknown;
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Uppercase initials from the first and last words of a display name
 * ("Sam Rivera" → "SR", "Sam" → "S"), falling back to the first letter of
 * the email local-part.
 */
export function deriveCityAuthInitials(displayName: string, email: string | null): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : '';
  const fromName = `${first}${last}`.toUpperCase();
  if (fromName.length > 0) return fromName;
  const fromEmail = email?.charAt(0)?.toUpperCase() ?? '';
  return fromEmail || '?';
}

/**
 * Display name from `/auth/me` fields. Prefers composed `name`, then
 * first+last, then the email local-part. Returns `null` when nothing real
 * is available (caller should treat as unauthenticated for chrome purposes).
 */
export function deriveCityAuthDisplayName(user: RawUserFields): string | null {
  const name = asNonEmptyString(user.name);
  if (name) return name;

  const first = asNonEmptyString(user.firstName);
  const last = asNonEmptyString(user.lastName);
  if (first && last) return `${first} ${last}`;
  if (first) return first;

  const email = asNonEmptyString(user.email);
  if (email) {
    const local = email.split('@')[0]?.trim();
    if (local && local.length > 0) return local;
  }
  return null;
}

/**
 * Honest meta / stats line. Joins present segments with ` · `; omits absent
 * ones. Never fabricates saved/following counts — those are app-surface
 * concepts and not on the city `/auth/me` payload today.
 */
export function deriveCityAuthMeta(user: RawUserFields): string {
  const parts: string[] = [];
  const location = asNonEmptyString(user.location);
  if (location) parts.push(location);

  const createdAt = asNonEmptyString(user.createdAt);
  if (createdAt) {
    const joined = new Date(createdAt);
    if (!Number.isNaN(joined.getTime())) {
      parts.push(
        `Joined ${joined.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })}`,
      );
    }
  }

  if (parts.length === 0) {
    const email = asNonEmptyString(user.email);
    if (email) parts.push(email);
  }

  return parts.join(' · ');
}

/**
 * Map an `/auth/me` `user` object (typed `unknown` from the shared probe) into
 * the city chrome identity shape. Returns `null` when the payload is missing
 * an id or a resolvable display name — chrome must not invent identity.
 */
export function cityAuthUserFromUnknown(user: unknown): CityAuthUser | null {
  if (typeof user !== 'object' || user === null) return null;
  const record = user as RawUserFields;
  const id = asNonEmptyString(record.id);
  if (!id) return null;

  const email = asNonEmptyString(record.email);
  const displayName = deriveCityAuthDisplayName(record);
  if (!displayName) return null;

  const avatarUrl = asNonEmptyString(record.avatar) ?? asNonEmptyString(record.picture);

  return {
    displayName,
    meta: deriveCityAuthMeta(record),
    initials: deriveCityAuthInitials(displayName, email),
    avatarUrl,
  };
}
